// ============================================================
// src/components/GigDayPreview.js
//
// A focused preview card shown when the performer clicks a
// date on the calendar. Designed for FUTURE events only.
//
// Differences from TodaysGigCard:
//   - No GPS / "away from home" check (you are not going yet)
//   - No live countdown -- shows "N days away" instead
//   - No push notification scheduling
//   - No prevGig / venue-to-venue prompt
//   - Displays the routing origin address clearly so the
//     performer knows which "home" is being used
//   - If a trip is active on that date, routing comes from
//     the stay address; otherwise from Settings home address
//
// ── PROPS ─────────────────────────────────────────────────────
//   gig           object    - the gig to preview
//   dateStr       string    - "YYYY-MM-DD" of the gig date
//   homeLat       number    - from useNotificationPrefs
//   homeLng       number    - from useNotificationPrefs
//   homeAddress   string    - display label for settings home
//   prepMinutes   number    - from useNotificationPrefs
//   musicTracks   array     - full library (for setlist display)
//
// GigDayPreviews (exported wrapper) renders one card per gig.
//
// ── ROUTING FIXES ─────────────────────────────────────────────
//
// FIX 1: buildMapsUrl now always uses the venue name + address
//   string for navigation links. Raw coords are NOT used because
//   stored coords may be inaccurate (saved without picking from
//   the Nominatim dropdown). Google/Apple Maps geocode the address
//   themselves and are far more accurate than Nominatim.
//
// FIX 2: geocodeAddress now accepts optional city and state
//   parameters and appends them to the Nominatim query string.
//   This prevents Nominatim from matching a street name in the
//   wrong city (e.g. "Park St" in Indian Land instead of Columbia).
//
// FIX 3: calculateRoute always geocodes the venue fresh from
//   address + city + state via Nominatim. Stored venue_lat /
//   venue_lng are intentionally NOT used for routing because they
//   may have been saved incorrectly. Nominatim with the city/state
//   hint is reliable, and Mapbox driving-traffic then handles live
//   traffic routing from those coords.
//
// ── THEME / WCAG NOTES ────────────────────────────────────────
//
// All colors live in .gdp-* classes in index.css (section 31).
// Zero inline color styles exist in this file. Only structural
// layout properties (display, grid, gap, margin, flex, position,
// overflow, cursor, animation) are set inline.
// ============================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTripMode, getTransitBufferMinutes } from '../hooks/useTripMode'
import { useMusicPlayer } from '../context/MusicPlayerContext'

// ── CONSTANTS ─────────────────────────────────────────────────
const TRAVEL_BUFFER_MINUTES = 20
const MAPBOX_TOKEN   = process.env.REACT_APP_MAPBOX_TOKEN
const MAPBOX_BASE    = 'https://api.mapbox.com/directions/v5/mapbox/driving-traffic'
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search'


// ── HELPERS ───────────────────────────────────────────────────

// Format h:mm AM/PM from a Date
function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

// Format drive seconds as "X hr Y min" or "Y min"
function formatDuration(s) {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h} hr ${m} min` : `${m} min`
}

// Format "HH:MM" as "h:mm AM/PM"
function fmt12(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

// Build a Date on the gig's specific calendar date + given hours/minutes.
// parseShowTime previously used new Date() which caused future gigs to
// appear as "show started". This version anchors to the gig's own date.
function parseShowTimeOnDate(timeStr, dateStr) {
  if (!timeStr || !dateStr) return null
  const [y, mo, d] = dateStr.split('-').map(Number)
  const t = timeStr.trim().toLowerCase()

  const makeDate = (h, m) => new Date(y, mo - 1, d, h, m, 0, 0)

  const withSeconds = t.match(/^(\d{1,2}):(\d{2}):\d{2}$/)
  if (withSeconds) return makeDate(parseInt(withSeconds[1], 10), parseInt(withSeconds[2], 10))

  const twelveHour = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
  if (twelveHour) {
    let h = parseInt(twelveHour[1], 10)
    const m = parseInt(twelveHour[2], 10)
    if (twelveHour[3] === 'am' && h === 12) h = 0
    if (twelveHour[3] === 'pm' && h !== 12) h += 12
    return makeDate(h, m)
  }

  const twentyFour = t.match(/^(\d{1,2}):(\d{2})$/)
  if (twentyFour) return makeDate(parseInt(twentyFour[1], 10), parseInt(twentyFour[2], 10))

  return null
}

// Days away from today to the gig date (always positive for future events)
function daysUntil(dateStr) {
  const [y, mo, d] = dateStr.split('-').map(Number)
  const gigDay = new Date(y, mo - 1, d)
  const today  = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((gigDay - today) / (1000 * 60 * 60 * 24))
}

// FIX 1: Build maps URL from venue name + address string only.
// Raw coords are never used here -- Google/Apple Maps geocode the
// address string themselves and are more accurate than stored coords.
function buildMapsUrl(gig) {
  const query = [gig.venue_name, gig.venue_address].filter(Boolean).join(', ')
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
  return isIOS
    ? `https://maps.apple.com/?q=${encodeURIComponent(query)}`
    : `https://maps.google.com/maps?q=${encodeURIComponent(query)}`
}

// FIX 2: Geocode via Nominatim with optional city/state hint to
// prevent matching the same street name in the wrong city.
async function geocodeAddress(address, city = '', state = '') {
  const hint  = [city, state].filter(Boolean).join(', ')
  const query = hint ? `${address}, ${hint}` : address
  const clean = (query || '')
    .replace(/\b(apt|apartment|unit|ste|suite|#|no\.?)\s*[a-z0-9-]+/gi, '')
    .replace(/\s{2,}/g, ' ').trim()
  const res  = await fetch(
    `${NOMINATIM_BASE}?q=${encodeURIComponent(clean)}&format=json&limit=1`,
    { headers: { 'Accept-Language': 'en' } }
  )
  const data = await res.json()
  if (!data?.length) throw new Error(`Address not found: ${address}`)
  return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) }
}

// Mapbox drive time using the driving-traffic profile so live traffic
// is factored into the leave-by calculation.
async function fetchDriveSeconds(fromLon, fromLat, toLon, toLat) {
  const res  = await fetch(
    `${MAPBOX_BASE}/${fromLon},${fromLat};${toLon},${toLat}` +
    `?access_token=${MAPBOX_TOKEN}&geometries=geojson&overview=false`
  )
  const data = await res.json()
  if (!data.routes?.length) throw new Error('No driving route found')
  return data.routes[0].duration
}


// ════════════════════════════════════════════════════════════════
// GigDayPreview
// Single card for one gig. No GPS, no countdown, no notifications.
// All color styling lives in .gdp-* CSS classes (index.css sec 31).
// ════════════════════════════════════════════════════════════════
function GigDayPreview({
  gig,
  dateStr,
  homeLat,
  homeLng,
  homeAddress,
  prepMinutes = 90,
  musicTracks = [],
  activeTrip = null,
  tripsLoading = false,
}) {
  // activeTrip and tripsLoading are passed down from GigDayPreviews,
  // which calls useTripMode() once to prevent a race condition where
  // routing fires before the trip fetch completes.

  // Routing origin: stay address if on a trip, otherwise Settings home
  const effectiveHomeLat     = activeTrip ? activeTrip.stay_lat     : homeLat
  const effectiveHomeLng     = activeTrip ? activeTrip.stay_lng     : homeLng
  const effectiveHomeAddress = activeTrip
    ? (activeTrip.stay_address || 'Home while on your trip address')
    : (homeAddress || 'Home address from Settings')

  // ── Music player (for setlist) ────────────────────────────────
  const {
    currentTrack, isPlaying, progress,
    queue: queueRaw, queueIndex: queueIndexRaw, repeatMode: repeatModeRaw,
    setQueue, playTrack, togglePlay,
    nextTrack: nextTrackFn, prevTrack: prevTrackFn, cycleRepeat: cycleRepeatFn,
  } = useMusicPlayer()

  const queue       = Array.isArray(queueRaw)           ? queueRaw      : []
  const queueIndex  = typeof queueIndexRaw === 'number' ? queueIndexRaw : -1
  const repeatMode  = repeatModeRaw || 'none'
  const nextTrack   = typeof nextTrackFn   === 'function' ? nextTrackFn   : () => {}
  const prevTrack   = typeof prevTrackFn   === 'function' ? prevTrackFn   : () => {}
  const cycleRepeat = typeof cycleRepeatFn === 'function' ? cycleRepeatFn : () => {}

  const isCurrentTrack = (track) => currentTrack?.url === (track.audio_url || track.url)

  // ── Routing state ─────────────────────────────────────────────
  const [routeStatus, setRouteStatus] = useState('idle')   // idle | routing | done | error
  const [routeError,  setRouteError]  = useState(null)
  const [leaveByTime, setLeaveByTime] = useState(null)
  const [arriveTime,  setArriveTime]  = useState(null)
  const [driveSecs,   setDriveSecs]   = useState(null)

  // ── Parsed show/call times ────────────────────────────────────
  const showTimeParsed = parseShowTimeOnDate(gig.time, dateStr)
  const callTimeParsed = useMemo(() => {
    return gig.call_time
      ? parseShowTimeOnDate(gig.call_time, dateStr)
      : showTimeParsed ? new Date(showTimeParsed.getTime() - 60 * 60 * 1000) : null
  }, [gig.call_time, dateStr, showTimeParsed])

  // ── Days away badge ───────────────────────────────────────────
  const days = daysUntil(dateStr)

  // ── Route calculation ─────────────────────────────────────────
  // FIX 3: Venue is always geocoded fresh from address + city + state.
  // Stored venue_lat / venue_lng are intentionally ignored because they
  // may have been saved without a city/state hint and could be wrong.
  const calculateRoute = useCallback(async () => {
    if (!effectiveHomeLat || !effectiveHomeLng || !callTimeParsed) return
    setRouteStatus('routing')
    setRouteError(null)

    try {
      if (!gig.venue_address) {
        setRouteError('No venue address saved for this gig.')
        setRouteStatus('error')
        return
      }

      const venueCoords = await geocodeAddress(
        gig.venue_address,
        gig.city  || '',
        gig.state || '',
      )

      const driveS = await fetchDriveSeconds(
        parseFloat(effectiveHomeLng), parseFloat(effectiveHomeLat),
        venueCoords.lon, venueCoords.lat
      )
      setDriveSecs(driveS)

      // How much prep time happens at the venue?
      const getReadyLoc = gig.get_ready_location || 'home'
      let venuePrepMs = 0
      if (getReadyLoc === 'venue')
        venuePrepMs = prepMinutes * 60 * 1000
      else if (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
        venuePrepMs = Math.round(prepMinutes * 0.5) * 60 * 1000
      else if (getReadyLoc === 'home_most' || getReadyLoc === 'other_most')
        venuePrepMs = Math.round(prepMinutes * 0.3) * 60 * 1000

      // Arrive time = call time minus venue prep
      const arrive  = new Date(callTimeParsed.getTime() - venuePrepMs)
      // Leave time  = arrive minus drive time minus 20 min buffer
      const leaveBy = new Date(arrive.getTime() - (driveS + TRAVEL_BUFFER_MINUTES * 60) * 1000)

      setArriveTime(arrive)
      setLeaveByTime(leaveBy)
      setRouteStatus('done')

    } catch (err) {
      console.error('[GigDayPreview] routing error:', err)
      setRouteError(err.message || 'Could not calculate route.')
      setRouteStatus('error')
    }
  }, [gig, callTimeParsed, effectiveHomeLat, effectiveHomeLng, prepMinutes])

  // Trigger routing once trips have loaded and coords are available
  useEffect(() => {
    if (routeStatus === 'idle' && !tripsLoading && effectiveHomeLat && effectiveHomeLng && callTimeParsed) {
      calculateRoute()
    }
  }, [routeStatus, tripsLoading, effectiveHomeLat, effectiveHomeLng, callTimeParsed, calculateRoute])

  // Reset routing if the effective home coords change (trip mode loaded async)
  const prevCoords = useRef({ lat: null, lng: null })
  useEffect(() => {
    const prev = prevCoords.current
    if (
      effectiveHomeLat && effectiveHomeLng &&
      (prev.lat !== effectiveHomeLat || prev.lng !== effectiveHomeLng)
    ) {
      setRouteStatus('idle')
      setLeaveByTime(null)
      setArriveTime(null)
      setDriveSecs(null)
    }
    prevCoords.current = { lat: effectiveHomeLat, lng: effectiveHomeLng }
  }, [effectiveHomeLat, effectiveHomeLng])

  // ── Setlist ───────────────────────────────────────────────────
  const setlistTracks = (gig.setlist || [])
    .map(entry => {
      const id = typeof entry === 'string' ? entry : entry?.id
      return musicTracks.find(t => t.id === id)
    })
    .filter(Boolean)

  const handleTrackClick = (track, index) => {
    if (isCurrentTrack(track)) { togglePlay(); return }
    if (typeof setQueue === 'function') {
      setQueue(setlistTracks.map(t => ({ name: t.title || t.name || 'Untitled', url: t.audio_url || t.url || '' })), index)
    } else {
      playTrack({ name: track.title || track.name || 'Untitled', url: track.audio_url || track.url || '' })
    }
  }

  // ── Get-ready display values ──────────────────────────────────
  const getReadyLoc    = gig.get_ready_location || 'home'
  const showsVenuePrep = ['venue', 'home_half', 'other_half', 'home_most', 'other_most'].includes(getReadyLoc)
  const venuePrepMins  = getReadyLoc === 'venue'
    ? prepMinutes
    : (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
    ? Math.round(prepMinutes / 2)
    : (getReadyLoc === 'home_most' || getReadyLoc === 'other_most')
    ? Math.round(prepMinutes * 0.3)
    : 0

  // Home prep start time = leaveByTime minus the home portion of prep
  let homePrepTime = null
  if (leaveByTime && getReadyLoc !== 'venue') {
    const homePrepMins = (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
      ? Math.round(prepMinutes * 0.5)
      : (getReadyLoc === 'home_most' || getReadyLoc === 'other_most')
      ? Math.round(prepMinutes * 0.7)
      : prepMinutes
    homePrepTime = new Date(leaveByTime.getTime() - homePrepMins * 60 * 1000)
  }

  // ── Return day terminal buffer note ──────────────────────────
  const isReturnDay = activeTrip && (() => {
    if (activeTrip.trip_type === 'multi_leg') {
      const nextLegDepartDate = activeTrip._activeLeg
        && activeTrip.legs?.[activeTrip._activeLeg._legIndex + 1]?.depart_at?.split('T')[0]
      return !!(nextLegDepartDate && nextLegDepartDate === dateStr)
    }
    return !!(activeTrip.depart_dest_at && activeTrip.depart_dest_at.split('T')[0] === dateStr)
  })()

  const returnTransportMode = activeTrip?.trip_type === 'multi_leg' && isReturnDay
    ? activeTrip.legs?.[activeTrip._activeLeg?._legIndex + 1]?.transport_mode
    : activeTrip?.transport_mode
  const transitBuffer = activeTrip ? getTransitBufferMinutes(returnTransportMode) : 0

  const terminalLabel = (() => {
    if (activeTrip?.trip_type === 'multi_leg' && activeTrip._departTerminal?.label) {
      return activeTrip._departTerminal.label
    }
    if (returnTransportMode === 'flight') return 'airport'
    if (returnTransportMode === 'bus')    return 'bus terminal'
    if (returnTransportMode === 'train')  return 'train station'
    return 'terminal'
  })()

  // ── Render ────────────────────────────────────────────────────
  // Zero inline color styles. All color tokens are in .gdp-* classes.
  // Inline styles here are structural only: display, grid, gap, margin,
  // flex, cursor, overflow, position, animation, font-family, font-size.
  return (
    <div className="gdp-card">

      {/* Decorative background orb -- no content */}
      <div aria-hidden="true" className="gdp-card__orb" />

      {/* Days-away badge */}
      <div className="gdp-badge">
        <i className="bi bi-calendar3" />
        {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days away`}
      </div>

      {/* Gig name */}
      <h2 className="gdp-heading">
        {gig.name || gig.venue_name}
      </h2>

      {/* Venue address / maps link */}
      <a
        href={buildMapsUrl(gig)}
        target="_blank"
        rel="noopener noreferrer"
        className="gdp-venue-link"
      >
        <i className="bi bi-geo-alt-fill" />
        {gig.venue_address || gig.venue_name}
        <i className="bi bi-box-arrow-up-right gdp-venue-link__ext" />
      </a>

      {/* Show time / call time / pay row */}
      <div className="gdp-time-row">

        <div>
          <p className="gdp-label">Show Time</p>
          <p className="gdp-show-time">
            {showTimeParsed ? formatTime(showTimeParsed) : (gig.time ? fmt12(gig.time.slice(0, 5)) : '--')}
          </p>
        </div>

        {callTimeParsed && (
          <div>
            <p className="gdp-label">
              <i className="bi bi-bell-fill" />
              {' '}Call Time
            </p>
            <p className="gdp-call-time">
              {formatTime(callTimeParsed)}
            </p>
            <p className="gdp-muted">
              {gig.call_time ? 'Set call time' : '1 hr before show (default)'}
            </p>
          </div>
        )}

        {gig.payment && (
          <div>
            <p className="gdp-label">Pay</p>
            <p className="gdp-pay">${gig.payment}</p>
          </div>
        )}
      </div>

      {/* Trip active banner */}
      {activeTrip && (
        <div className="gdp-trip-banner">
          <div className="gdp-trip-banner__heading">
            <i className="bi bi-suitcase-fill" />
            Traveling - {activeTrip.destination_label}
          </div>
          <div className="gdp-trip-banner__body">
            Routing from your Trip Home, not your home address in Settings.
            {isReturnDay && transitBuffer > 0 && (
              <span> Departure day: leave an extra {transitBuffer} min early for the {terminalLabel}.</span>
            )}
          </div>
        </div>
      )}

      {/* Routing origin -- always shown so performer knows which address is used */}
      <div className="gdp-origin">
        <i className={`bi bi-house-fill gdp-origin__icon${activeTrip ? ' gdp-origin__icon--trip' : ''}`} />
        <div>
          <div className="gdp-origin__label">Routing from</div>
          <div className="gdp-origin__address">
            {effectiveHomeAddress || 'Set your home address in Settings to see leave times'}
          </div>
        </div>
      </div>

      {/* Get ready + leave time grid */}
      <div className="gdp-timing-grid">

        {/* Get ready tile (when not getting ready entirely at venue) */}
        {homePrepTime && getReadyLoc !== 'venue' && (
          <div className="gdp-block gdp-block--ready">
            <p className="gdp-label">
              <i className="bi bi-stars" />
              {' '}Start Getting Ready
            </p>
            <p className="gdp-big-number gdp-big-number--ready">
              {formatTime(homePrepTime)}
            </p>
            <p className="gdp-muted">
              {getReadyLoc === 'home'
                ? `${prepMinutes} min to get ready`
                : (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
                ? `${Math.round(prepMinutes * 0.5)} min here, then ${Math.round(prepMinutes * 0.5)} min at venue`
                : `${Math.round(prepMinutes * 0.7)} min here, then ${Math.round(prepMinutes * 0.3)} min at venue`}
            </p>
          </div>
        )}

        {/* Arrive-at-venue tile (when getting ready entirely there) */}
        {arriveTime && getReadyLoc === 'venue' && (
          <div className="gdp-block gdp-block--venue">
            <p className="gdp-label">
              <i className="bi bi-stars" />
              {' '}Arrive and Start Prep
            </p>
            <p className="gdp-big-number gdp-big-number--venue">
              {formatTime(arriveTime)}
            </p>
            <p className="gdp-muted">
              {prepMinutes} min of prep at the venue
            </p>
          </div>
        )}

        {/* Leave home by tile */}
        <div
          className={`gdp-block${routeStatus === 'error' ? ' gdp-block--clickable' : ''}`}
          onClick={routeStatus === 'error' ? calculateRoute : undefined}
          title={routeStatus === 'error' ? 'Tap to retry' : undefined}
        >
          <p className="gdp-label">
            <i className="bi bi-car-front-fill" />
            {' '}Leave {activeTrip ? 'Trip Home' : 'Home'} By
            {routeStatus === 'done' && (
              <span className="gdp-label__sub">
                {' '}to {gig.venue_name || 'venue'}
              </span>
            )}
          </p>

          {(routeStatus === 'idle' || routeStatus === 'routing') && (
            <p className="gdp-route-pending">
              {routeStatus === 'routing'
                ? <><i className="bi bi-arrow-repeat gdp-spin" />Routing...</>
                : <span className="gdp-route-waiting">Waiting...</span>
              }
            </p>
          )}

          {routeStatus === 'error' && (
            <p className="gdp-route-error">
              <i className="bi bi-exclamation-triangle-fill" />
              {' '}{routeError}
            </p>
          )}

          {routeStatus === 'done' && leaveByTime && (
            <>
              <p className="gdp-big-number gdp-big-number--leave">
                {formatTime(leaveByTime)}
              </p>
              <p className="gdp-drive-sub">
                {driveSecs ? formatDuration(driveSecs) : ''} drive + {TRAVEL_BUFFER_MINUTES} min buffer to arrive by call time
              </p>
            </>
          )}
        </div>

        {/* Venue prep minutes tile (half/most get-ready modes) */}
        {showsVenuePrep && venuePrepMins > 0 && getReadyLoc !== 'venue' && (
          <div className="gdp-block gdp-block--venueprep">
            <p className="gdp-label">
              <i className="bi bi-building" />
              {' '}Finish Prep at Venue
            </p>
            <p className="gdp-venueprep-value">
              {venuePrepMins} min
            </p>
          </div>
        )}
      </div>

      {/* Setlist */}
      {setlistTracks.length > 0 && (
        <div className="gdp-block gdp-setlist">
          <p className="gdp-label gdp-setlist__header">
            <i className="bi bi-music-note-list" />
            {' '}Setlist - {setlistTracks.length} track{setlistTracks.length !== 1 ? 's' : ''}
          </p>

          <div className="gdp-setlist__tracks">
            {setlistTracks.map((track, index) => {
              const isThis     = isCurrentTrack(track)
              const isPlaying_ = isThis && isPlaying
              return (
                <div key={track.id} className="gdp-setlist__track">
                  <div className="gdp-setlist__track-row">
                    <button
                      onClick={() => handleTrackClick(track, index)}
                      className={`gdp-setlist__play-btn${isThis ? ' gdp-setlist__play-btn--active' : ''}`}
                    >
                      <i className={isPlaying_ ? 'bi bi-pause-fill' : 'bi bi-play-fill'} />
                    </button>

                    <div className="gdp-setlist__track-info">
                      <p className={`gdp-setlist__track-name${isThis ? ' gdp-setlist__track-name--active' : ''}`}>
                        {index + 1}. {track.title || track.name || 'Untitled Track'}
                      </p>
                      {track.artist && (
                        <p className="gdp-muted gdp-setlist__artist">{track.artist}</p>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for currently playing track */}
                  {isThis && (
                    <div className="gdp-setlist__progress-track">
                      <div
                        className="gdp-setlist__progress-fill"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Mini player */}
          {currentTrack && queue.length > 0 && (
            <div className="gdp-miniplayer">
              <div className="gdp-miniplayer__info">
                <p className="gdp-muted gdp-miniplayer__status">
                  {isPlaying ? 'Now Playing' : 'Paused'}
                </p>
                <p className="gdp-miniplayer__name">{currentTrack.name}</p>
              </div>

              <div className="gdp-miniplayer__controls">
                <button
                  onClick={cycleRepeat}
                  className={`gdp-miniplayer__btn${repeatMode !== 'none' ? ' gdp-miniplayer__btn--active' : ''}`}
                >
                  <i className={repeatMode === 'one' ? 'bi bi-repeat-1' : 'bi bi-arrow-repeat'} />
                </button>

                <button
                  onClick={prevTrack}
                  disabled={queueIndex <= 0 && repeatMode !== 'all'}
                  className="gdp-miniplayer__btn"
                >
                  <i className="bi bi-skip-start-fill" />
                </button>

                <button onClick={togglePlay} className="gdp-miniplayer__play-btn">
                  <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`} />
                </button>

                <button
                  onClick={nextTrack}
                  disabled={queueIndex >= queue.length - 1 && repeatMode !== 'all'}
                  className="gdp-miniplayer__btn"
                >
                  <i className="bi bi-skip-end-fill" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View full gig link */}
      <Link to={`/gigs/${gig.id}`} className="gdp-full-link">
        <i className="bi bi-arrow-right-circle-fill" /> View full gig
      </Link>

      <style>{`@keyframes gdp-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}


// ════════════════════════════════════════════════════════════════
// GigDayPreviews -- wrapper that renders one GigDayPreview per gig.
// Used by DayGigModal in calendar.js.
//
//   <GigDayPreviews
//     gigs={selectedDay.gigs}
//     dateStr={selectedDay.dateStr}
//     homeLat={homeLat}
//     homeLng={homeLng}
//     homeAddress={geocodedLabel || homeAddress}
//     prepMinutes={prepMinutes}
//     musicTracks={musicTracks}
//   />
// ════════════════════════════════════════════════════════════════
export function GigDayPreviews({
  gigs = [],
  dateStr,
  homeLat,
  homeLng,
  homeAddress,
  prepMinutes = 90,
  musicTracks = [],
}) {
  // Resolve the active trip once here so every GigDayPreview card gets the
  // same already-loaded value. Each card having its own useTripMode() call
  // caused a race where routing fired before the trip fetch completed.
  const { getActiveTripForDate, loading: tripsLoading } = useTripMode()
  const activeTrip = getActiveTripForDate(dateStr)

  if (!gigs || gigs.length === 0) return null

  return (
    <div className="gdp-list">
      {gigs.map((gig, index) => (
        <div key={gig.id}>
          {gigs.length > 1 && (
            <div className="gdp-show-counter">
              Show {index + 1} of {gigs.length}
            </div>
          )}
          <GigDayPreview
            gig={gig}
            dateStr={dateStr}
            homeLat={homeLat}
            homeLng={homeLng}
            homeAddress={homeAddress}
            prepMinutes={prepMinutes}
            musicTracks={musicTracks}
            activeTrip={activeTrip}
            tripsLoading={tripsLoading}
          />
        </div>
      ))}
    </div>
  )
}

export default GigDayPreview